import { FiBook } from 'react-icons/fi';

export const awlSublist2Data = {
  courseId: 'awl-sublist-2',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 2',
  icon: FiBook,
  overview: {
    impact: 'Expand your academic vocabulary range with essential Sublist 2 words',
    focus: '12 key words from Coxhead\'s AWL Sublist 2',
    uses: 'Reading, Writing Task 2',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word Meanings and Usage',
      content: '<p>Sublist 2 builds on the foundation of Sublist 1 with slightly less frequent but still essential academic words. These words appear regularly in IELTS Reading passages and are expected in Band 7+ essays.</p>',
      points: [
        'These words are the second most frequent group in academic English',
        'They appear across disciplines including social sciences, business, and natural sciences',
        'Mastering them significantly improves your Lexical Resource score',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['achieve', 'To succeed in reaching a goal through effort', 'Students who set clear targets are more likely to achieve academic success.'],
          ['acquire', 'To gain or obtain something', 'Children acquire language skills naturally through exposure.'],
          ['administrate', 'To manage or organise the affairs of something', 'Local councils administrate public services in the region.'],
          ['affect', 'To have an influence on; to produce a change in', 'Air pollution significantly affects public health in urban areas.'],
          ['appropriate', 'Suitable or proper in the circumstances', 'It is appropriate to use formal language in academic writing.'],
          ['aspect', 'A particular part or feature of something', 'One important aspect of education is critical thinking.'],
          ['assist', 'To help or give support', 'Technology can assist students with learning difficulties.'],
          ['category', 'A class or division of things with shared characteristics', 'The responses were divided into three main categories.'],
          ['chapter', 'A main division of a book or a period of time', 'This chapter examines the effects of globalisation on trade.'],
          ['commission', 'A group given authority to perform a task; a fee for service', 'The government set up a commission to investigate the issue.'],
          ['community', 'A group of people living together or sharing characteristics', 'The local community organised a campaign to improve recycling.'],
          ['complex', 'Consisting of many interconnected parts; not simple', 'Climate change is a complex issue with no single solution.'],
        ],
      },
    },
    {
      title: 'Word Families',
      content: '<p>Understanding word families helps you vary your language in Writing and avoid repetition. IELTS examiners look for accurate word-form usage across different grammatical contexts.</p>',
      points: [
        'Changing word forms allows you to paraphrase effectively in Reading and Writing',
        'Incorrect word forms are a common error that reduces your Grammar score',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['achieve', 'achievement, achiever', 'achieve', 'achievable'],
          ['acquire', 'acquisition', 'acquire', 'acquired'],
          ['administrate', 'administration, administrator', 'administrate', 'administrative'],
          ['affect', 'effect (related)', 'affect', 'affected, affective'],
          ['appropriate', 'appropriateness', 'appropriate', 'appropriate, appropriately'],
          ['aspect', 'aspect', '—', '—'],
          ['assist', 'assistance, assistant', 'assist', '—'],
          ['category', 'category, categorisation', 'categorise', 'categorical, categorically'],
          ['commission', 'commission, commissioner', 'commission', 'commissioned'],
          ['community', 'community', '—', 'communal, communally'],
          ['complex', 'complexity', '—', 'complex'],
        ],
      },
    },
    {
      title: 'Common Collocations',
      content: '<p>Learning collocations makes your English sound natural and helps you write more fluently under exam time pressure.</p>',
      points: [
        'Collocations are tested indirectly in IELTS Reading (matching and gap-fill tasks)',
        'Using accurate collocations in Writing demonstrates a strong command of vocabulary',
      ],
      examples: [
        { label: 'achieve', text: 'achieve a goal, achieve success, achieve results, achieve a balance' },
        { label: 'acquire', text: 'acquire knowledge, acquire skills, acquire a language, newly acquired' },
        { label: 'affect', text: 'adversely affect, directly affect, significantly affect, emotionally affected' },
        { label: 'appropriate', text: 'entirely appropriate, appropriate measures, appropriate behaviour, deemed appropriate' },
        { label: 'aspect', text: 'key aspect, important aspect, every aspect of, negative aspect' },
        { label: 'assist', text: 'assist with, assist in (doing), provide assistance, financial assistance' },
        { label: 'category', text: 'fall into a category, broad category, main category, age category' },
        { label: 'commission', text: 'set up a commission, European Commission, earn a commission' },
        { label: 'community', text: 'local community, community service, sense of community, online community' },
        { label: 'complex', text: 'highly complex, increasingly complex, complex issue, complex system' },
      ],
    },
    {
      title: 'Using AWL Words in IELTS Writing',
      content: '<p>Sublist 2 words are particularly useful for discussing processes, evaluations, and social issues in Task 2 essays.</p>',
      points: [
        'Use "affect" instead of informal "change" or "impact" (as a noun used as a verb)',
        'Replace "help" with "assist" and "get" with "acquire" for a more academic register',
        'Describe problems as "complex" rather than "difficult" or "hard"',
      ],
      examples: [
        { label: 'Weak sentence', text: 'Pollution changes how healthy people are in big cities.' },
        { label: 'Strong sentence', text: 'Air pollution significantly affects public health in urban communities.' },
        { label: 'Weak sentence', text: 'Sorting the answers into groups helped the researchers.' },
        { label: 'Strong sentence', text: 'Categorising the responses assisted the researchers in identifying key patterns.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Word to Definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        { text: 'What does "acquire" mean?', options: ['To lose gradually', 'To gain or obtain something', 'To give away freely'], answer: 'To gain or obtain something', explanation: '"Acquire" means to obtain, often through effort or experience, e.g. "acquire new skills".' },
        { text: 'What does "affect" mean?', options: ['To have an influence on something', 'To pretend to feel something', 'To complete a task'], answer: 'To have an influence on something', explanation: '"Affect" (verb) means to influence or produce a change. Don\'t confuse it with "effect" (noun).' },
        { text: 'What does "complex" mean?', options: ['Simple and easy to understand', 'Consisting of many interconnected parts', 'Related to a building'], answer: 'Consisting of many interconnected parts', explanation: '"Complex" describes something with many interrelated elements, making it difficult to understand.' },
        { text: 'What does "commission" mean?', options: ['A group given official authority for a task', 'A type of academic essay', 'A financial punishment'], answer: 'A group given official authority for a task', explanation: 'A "commission" is an official body set up to investigate or manage something.' },
        { text: 'What does "appropriate" mean?', options: ['Extremely expensive', 'Suitable or proper for the situation', 'Belonging to someone else'], answer: 'Suitable or proper for the situation', explanation: '"Appropriate" means fitting or suitable for a particular context or purpose.' },
      ],
    },
    {
      title: 'Complete the Sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct word from Sublist 2.',
      questions: [
        { text: 'With hard work and determination, she was able to ___ her academic goals.', answer: 'achieve', explanation: '"Achieve" collocates naturally with "goals" and implies effort and success.' },
        { text: 'The ___ worked together to clean up the local park.', answer: 'community', explanation: '"Community" refers to a group of people in a locality who share common interests.' },
        { text: 'Diet and exercise are both important ___ of a healthy lifestyle.', answer: 'aspects', explanation: '"Aspects" means particular parts or features of something — note the plural form.' },
        { text: 'The teaching assistant was employed to ___ students with special needs.', answer: 'assist', explanation: '"Assist" is the formal equivalent of "help" and fits academic register.' },
      ],
    },
    {
      title: 'Choose the Correct Word Form',
      type: 'select',
      instructions: 'Select the grammatically correct form of the word for each sentence.',
      questions: [
        { text: 'Her greatest ___ was winning the national science prize. (achieve)', options: ['achievement', 'achievable', 'achieve'], answer: 'achievement', explanation: 'A noun is needed after "greatest" — "achievement" is the noun form.' },
        { text: 'The ___ of a second language takes years of practice. (acquire)', options: ['acquired', 'acquisition', 'acquire'], answer: 'acquisition', explanation: '"The acquisition of" requires a noun — "acquisition" is correct.' },
        { text: 'The problem is far more ___ than it first appears. (complex)', options: ['complexity', 'complex', 'complexly'], answer: 'complex', explanation: 'An adjective is needed after "more" in a comparative structure.' },
        { text: 'The decision was made by the ___ team. (administrate)', options: ['administration', 'administrative', 'administrate'], answer: 'administrative', explanation: 'An adjective is needed before the noun "team" — "administrative" modifies it.' },
      ],
    },
    {
      title: 'Use Words in Sentences',
      type: 'writing',
      instructions: 'Write a sentence using the given word appropriately in an academic context.',
      questions: [
        { text: 'Write a sentence using "affect" about the impact of social media.', example: 'Excessive use of social media can adversely affect the mental health of young people.', tip: 'Use a collocation like "adversely affect" or "significantly affect" for academic tone.' },
        { text: 'Write a sentence using "category" in the context of research.', example: 'The survey responses were classified into three broad categories based on age group.', tip: 'Try "fall into a category" or "broad/main category" for natural phrasing.' },
        { text: 'Write a sentence using "complex" to describe a global issue.', example: 'The relationship between economic growth and environmental protection is highly complex.', tip: 'Pair with "highly", "increasingly", or "inherently" for an advanced collocation.' },
      ],
    },
  ],
  tips: [
    'Pay attention to the difference between "affect" (verb) and "effect" (noun) — this is a common IELTS trap',
    'Learn "achieve" collocations: achieve a goal, achieve success, achieve results',
    'Use "appropriate" and "complex" frequently — they fit almost any IELTS topic',
    'Read news articles about social issues to see Sublist 2 words in authentic contexts',
  ],
  dailyPractice: [
    'Choose 3 Sublist 2 words and write a short IELTS paragraph using all of them',
    'Read an editorial article and underline any Sublist 2 words you recognise',
    'Practise transforming sentences: replace informal words with Sublist 2 equivalents',
    'Test yourself by writing word families from memory for 5 target words',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 2 vocabulary course covering 12 key academic words: achieve, acquire, administrate, affect, appropriate, aspect, assist, category, chapter, commission, community, complex. Includes word meanings, word families, collocations, and IELTS Writing integration.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist2Data,
      title: awlSublist2Data.title + ' (Foundations)',
      overview: { ...awlSublist2Data.overview, duration: '15-20 minutes' },
      sections: awlSublist2Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist2Data.sections.length / 2))),
      exercises: awlSublist2Data.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist2Data,
      title: awlSublist2Data.title + ' (Advanced)',
      overview: { ...awlSublist2Data.overview, duration: '30-35 minutes' },
      sections: awlSublist2Data.sections,
      exercises: awlSublist2Data.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist2Data.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return awlSublist2Data;
}
